// /signup: paid plans with a free trial. 1 Details (plan, name, email) → 2 Verify email → 3 Password →
// 4 Card (a temporary hold for the first month, released right away) → the trial starts and they're in.
// Contract: packages/shared/src/signup.ts. Flow rules and URL entry points: ./signup/flow.ts.
//
//   /signup?plan=pro          start with Pro picked
//   /signup#token=…           the email link → POST /email → a signup session (kept in sessionStorage)
//   /signup#session=…         a pending signup that signed in at /login
//   /signup?card=return&…     back from a redirect-based card check (3-D Secure): activate
//   /signup#upgrade=…         a free-trial account's paid offer (UpgradeOffer)

import { TRIAL_DAYS } from "@gf/shared";
import type { SignupState } from "@gf/shared/signup";
import { ApiError } from "../../lib/api";
import { Button, buttonClass, Callout, Spinner } from "@gf/ui";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { trackCompletedSignup, trackSignupStep } from "../../lib/analytics";
import { AuthHeading, AuthLayout } from "./AuthLayout";
import { UpgradeOffer } from "./UpgradeOffer";
import { signupApi } from "./signup/api";
import { CardStep } from "./signup/CardStep";
import { DetailsStep, type Details } from "./signup/DetailsStep";
import { CheckEmailStep, RecoverStep } from "./signup/EmailSteps";
import {
  clearStoredSession,
  DEFAULT_PLAN,
  errorText,
  readStoredSession,
  signupEntry,
  signupFailure,
  stepFor,
  storeSession,
  type SignupView,
} from "./signup/flow";
import { PasswordStep } from "./signup/PasswordStep";
import { SignInLine } from "./signup/SignupBits";
import { SignupSteps } from "./signup/SignupSteps";
import "./signup/signup.css";

type Screen = { view: SignupView } | { view: "boot"; message: string } | { view: "broken"; message: string };

interface Notice {
  tone: "info" | "warn" | "danger";
  text: string;
}

const EXPIRED = "Your signup link has expired. Enter your email and we'll send a fresh one.";

/** Drop tokens, sessions and Stripe's return parameters from the address bar. */
function cleanUrl() {
  window.history.replaceState(null, "", "/signup");
}

export function SignupPage() {
  const [entry] = useState(() => signupEntry(window.location, readStoredSession()));
  const [screen, setScreen] = useState<Screen>(() =>
    entry.kind === "email"
      ? { view: "boot", message: "Verifying your email…" }
      : entry.kind === "card_return"
        ? { view: "boot", message: "Checking your card…" }
        : entry.kind === "resume"
          ? { view: "boot", message: "Picking up where you left off…" }
          : { view: "details" },
  );
  const [details, setDetails] = useState<Details>({
    plan: entry.kind === "start" ? entry.plan : DEFAULT_PLAN,
    name: "",
    email: "",
    code: entry.kind === "start" ? entry.code : "",
  });
  const [session, setSession] = useState("");
  const [signup, setSignup] = useState<SignupState | null>(null);
  const signupRef = useRef(signup);
  signupRef.current = signup;
  const [notice, setNotice] = useState<Notice | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardAttempt, setCardAttempt] = useState(0);
  const [activationError, setActivationError] = useState<{ message: string; account: boolean; promo: boolean } | null>(null);
  const [dropping, setDropping] = useState(false);
  const started = useRef(false);

  const show = useCallback((next: Screen, nextNotice: Notice | null = null) => {
    setScreen(next);
    setNotice(nextNotice);
  }, []);

  /** The signup session is gone (expired, or used up): start again from a fresh email link. */
  const expire = useCallback(() => {
    clearStoredSession();
    setSession("");
    const known = signupRef.current?.email;
    if (known) setDetails((d) => ({ ...d, email: known }));
    show({ view: "recover" }, { tone: "warn", text: EXPIRED });
  }, [show]);

  const adopt = useCallback(
    (key: string, state: SignupState) => {
      storeSession(key);
      setSession(key);
      setSignup(state);
      setDetails((d) => ({ ...d, plan: state.plan, name: state.name, email: state.email, code: state.discount?.code ?? d.code }));
      show({ view: state.stage === "card" ? "card" : "password" });
    },
    [show],
  );

  const resume = useCallback(
    async (key: string, opts: { cardError?: string } = {}) => {
      storeSession(key);
      setSession(key);
      show({ view: "boot", message: "Picking up where you left off…" });
      try {
        const state = await signupApi.status(key);
        if (opts.cardError) {
          setCardError(opts.cardError);
          setCardAttempt((n) => n + 1);
        }
        adopt(key, state);
      } catch (err) {
        if (signupFailure(err) === "expired") expire();
        else show({ view: "broken", message: errorText(err, "We couldn't load your signup.") });
      }
    },
    [adopt, expire, show],
  );

  /** The hold is in place: the API checks it, releases it, starts the trial and signs them in. */
  const activate = useCallback(
    async (key: string) => {
      setSession(key);
      setActivationError(null);
      show({ view: "activating" });
      try {
        const res = await signupApi.activate(key);
        clearStoredSession();
        try {
          await trackCompletedSignup();
        } catch {
          /* Measurement never gates account access. */
        }
        window.location.assign(res.url || "/");
      } catch (err) {
        const failure = signupFailure(err);
        if (failure === "expired") return expire();
        if (failure === "card") {
          const message = errorText(err, "That card didn't check out. Try another card.");
          if (!signupRef.current) return resume(key, { cardError: message });
          setCardError(message);
          setCardAttempt((n) => n + 1);
          return show({ view: "card" });
        }
        setActivationError({
          message: errorText(err, "We couldn't finish setting up your account. Try again."),
          account: failure === "account",
          promo: err instanceof ApiError && err.code === "promo_invalid",
        });
      }
    },
    [expire, resume, show],
  );

  // Where this visit starts. Runs once (StrictMode mounts twice; the email token works once).
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    switch (entry.kind) {
      case "email":
        cleanUrl();
        void (async () => {
          try {
            const res = await signupApi.email(entry.token);
            const { session: key, ...state } = res;
            trackSignupStep("email_verified");
            adopt(key, state);
          } catch (err) {
            show(
              { view: "recover" },
              { tone: "danger", text: errorText(err, "That link has expired or was already used. Enter your email and we'll send a fresh one.") },
            );
          }
        })();
        return;
      case "resume":
        cleanUrl();
        void resume(entry.session);
        return;
      case "card_return":
        cleanUrl();
        if (!entry.session) {
          // Stripe sent them back to a different tab (some banking apps do): this tab has no session.
          show(
            { view: "recover" },
            {
              tone: "info",
              text: "Your bank checked your card in another window. Sign in with your email and password to finish, or get a fresh link below.",
            },
          );
        } else if (entry.failed) {
          void resume(entry.session, { cardError: "Your bank didn't confirm the card. Try again, or use a different card." });
        } else {
          void activate(entry.session);
        }
        return;
      default:
        return;
    }
  }, [entry, adopt, resume, activate, show]);

  if (entry.kind === "upgrade") return <UpgradeOffer token={entry.token} />;

  const { title, lede } = heading(screen, details.email);
  const step = screen.view === "boot" || screen.view === "broken" ? null : stepFor(screen.view);

  let body: ReactNode = null;
  switch (screen.view) {
    case "boot":
      body = (
        <div className="signup-wait">
          <Spinner size={28} label={screen.message} />
        </div>
      );
      break;
    case "broken":
      body = (
        <div className="gf-stack">
          <Callout tone="danger">{screen.message}</Callout>
          <div className="signup-actions">
            <Button variant="primary" onClick={() => void resume(session || readStoredSession() || "")} disabled={!session && !readStoredSession()}>
              Try again
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                clearStoredSession();
                setSession("");
                setSignup(null);
                show({ view: "details" });
              }}
            >
              Start over
            </Button>
          </div>
        </div>
      );
      break;
    case "details":
      body = (
        <DetailsStep
          initial={details}
          onSent={(d) => {
            setDetails(d);
            show({ view: "check_email" });
          }}
        />
      );
      break;
    case "check_email":
      body = <CheckEmailStep email={details.email} onChangeEmail={() => show({ view: "details" })} />;
      break;
    case "recover":
      body = (
        <RecoverStep
          initialEmail={details.email}
          onSent={(email) => {
            setDetails((d) => ({ ...d, email }));
            show({ view: "check_email" });
          }}
        />
      );
      break;
    case "password":
      body = signup ? (
        <PasswordStep session={session} email={signup.email} onSaved={(state) => adopt(session, state)} onExpired={expire} />
      ) : null;
      break;
    case "card":
      body = signup ? (
        <CardStep
          session={session}
          signup={signup}
          attempt={cardAttempt}
          cardError={cardError}
          onSignupChange={(state) => {
            setSignup(state);
            setDetails((d) => ({ ...d, plan: state.plan, name: state.name, email: state.email }));
          }}
          onConfirmed={() => void activate(session)}
          onExpired={expire}
        />
      ) : null;
      break;
    case "activating":
      body = activationError ? (
        <div className="gf-stack">
          <Callout tone="danger" title={activationError.account ? "You already have an account" : "Your card checked out, but we couldn't finish"}>
            {activationError.message}
          </Callout>
          {activationError.account ? (
            <Link className={buttonClass({ variant: "primary", size: "lg", block: true })} to="/login" state={{ email: details.email }}>
              Sign in
            </Link>
          ) : activationError.promo ? (
            // The card is already confirmed (the hold is in place): drop the code and start the trial.
            <Button
              variant="primary"
              size="lg"
              block
              loading={dropping}
              onClick={() => {
                setDropping(true);
                void signupApi
                  .promo(session, null)
                  .then((state) => {
                    setSignup(state);
                    setActivationError(null);
                    return activate(session);
                  })
                  .catch((err) => setActivationError({ message: errorText(err), account: false, promo: true }))
                  .finally(() => setDropping(false));
              }}
            >
              Start my trial without the code
            </Button>
          ) : (
            <Button variant="primary" size="lg" block onClick={() => void activate(session)}>
              Try again
            </Button>
          )}
        </div>
      ) : (
        <div className="signup-wait">
          <Spinner size={28} label="Starting your trial" />
          <p>Confirming the hold, then releasing it.</p>
        </div>
      );
      break;
  }

  return (
    <AuthLayout title="Start your free trial">
      <div className="signup">
        {step ? <SignupSteps current={step} /> : null}
        <AuthHeading title={title} lede={lede} />
        {notice ? <Callout tone={notice.tone}>{notice.text}</Callout> : null}
        {body}
        {screen.view !== "activating" ? <SignInLine email={details.email || undefined} /> : null}
      </div>
    </AuthLayout>
  );
}

function heading(screen: Screen, email: string): { title: string; lede?: ReactNode } {
  switch (screen.view) {
    case "boot":
      return { title: "One moment", lede: screen.message };
    case "broken":
      return { title: "We hit a snag", lede: "Your progress is saved. Try again in a moment." };
    case "details":
      return { title: "Start your free trial", lede: "Pick a plan, verify your email, set a password, add a card. Done." };
    case "check_email":
      return {
        title: "Check your email",
        lede: (
          <>
            We sent a verification link to <b>{email}</b>. Click it to keep going.
          </>
        ),
      };
    case "recover":
      return {
        title: "Get a fresh link",
        lede: "Enter the email you signed up with and we'll send a new link. Already chose a password? Sign in and you'll land right back here.",
      };
    case "password":
      return {
        title: "Create your password",
        lede: (
          <>
            Email verified: <b>{email}</b>. You'll use this password for the app and the Chrome extension.
          </>
        ),
      };
    case "card":
      return { title: "Add your card", lede: `Last step. Your ${TRIAL_DAYS}-day free trial starts as soon as your card checks out.` };
    case "activating":
      return { title: "Starting your trial", lede: "Don't close this tab. It takes a few seconds." };
  }
}
